import React, { type FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { LinkedLayer, TemplateVariant } from '@/api/types';
import { LayerIcon } from '@/common/layer-icon';
import { FRAME_OVERLAY_Z_INDEX } from '@/common/z-index';
import { useTemplateEditorStore } from './store';
import { type LayerRect, LayerType, type TextLayerProps, fieldToLayerType } from './types';

const SELECTABLE_NODE_TYPES = ['text', 'tspan', 'image'];

export interface FrameProps {
    variant: TemplateVariant;
    width?: number;
}

function getTextLayerProps(parentNode: SVGElement): TextLayerProps {
    // usvg always adds a tspan element inside the text element
    // so we can use it to get the font properties
    const tspan = parentNode.querySelector('tspan[font-family]') || parentNode;
    const computedStyles = window.getComputedStyle(tspan);
    const fontSize = Number.parseFloat(computedStyles.fontSize);
    // remove quotes if name has space otherwise can fail to match in etcher
    const fontFamily = computedStyles.fontFamily.replace(/['"]/g, '');
    const fontWeight = Number.parseInt(computedStyles.fontWeight, 10);
    const fontStyle = computedStyles.fontStyle;
    const lineHeight = Number.parseFloat(computedStyles.lineHeight) / fontSize;
    const fill = computedStyles.fill;

    return {
        text: parentNode.textContent || '',
        fontSize,
        fontFamily,
        fontWeight,
        fontStyle,
        lineHeight,
        fill,
    };
}

export const Frame: FC<FrameProps> = ({ variant, width }) => {
    const svgContent = useTemplateEditorStore((s) => s.svgFiles[variant.svgFile.file]);
    const editedFieldData = useTemplateEditorStore((s) => s.editedFieldData);
    const setHighlightedLayer = useTemplateEditorStore((s) => s.setHighlightedLayer);
    const setSelectedLayer = useTemplateEditorStore((s) => s.setSelectedLayer);

    const overlayRef = useRef<SVGSVGElement>(null);
    const highlightRectRef = useRef<SVGRectElement>(null);
    const linkedLayerRef = useRef<SVGRectElement>(null);
    const selectRectRef = useRef<SVGRectElement>(null);

    const [linkedLayer, setLinkedLayer] = useState<LinkedLayer | null>(null);
    const [outlineRects, setOutlineRects] = useState<LayerRect[]>([]);
    const [showOutlineFor, setShowOutlineFor] = useState<LayerType | null>(null);
    const [zoom, setZoom] = useState<number>(1);

    // This is a workaround to ensure that the SVG content is rendered before we try to access the SVG elements.
    // Any tasks that need to be executed on the SVG element should be added to this array.
    const [svgScheduledTasks, setSvgScheduledTasks] = useState<((svg: HTMLDivElement) => void)[]>([]);
    const svgRef = useCallback(
        (node: HTMLDivElement) => {
            if (node) {
                const svg = node.querySelector('svg') as SVGElement;
                let svgWidth = variant.svgFile.width;
                let svgHeight = variant.svgFile.height;
                let ratio = 1;
                if (width) {
                    ratio = width / svgWidth;
                    svgHeight = ratio * svgHeight;
                    svgWidth = width;
                }
                setZoom(ratio);
                svg.setAttribute('width', svgWidth.toString());
                svg.setAttribute('height', svgHeight.toString());

                const textNodes = svg.querySelectorAll('text');
                const outlines: LayerRect[] = [];
                for (const node of textNodes) {
                    outlines.push(getElementRect(node));
                }
                setOutlineRects(outlines);

                for (const task of svgScheduledTasks) {
                    task(node);
                }
            }
        },
        [svgScheduledTasks, variant, width],
    );

    const getElementId = (el: SVGElement): string => {
        const id = el.dataset.ntglLayerId;
        if (!id) {
            throw new Error('Layer ID is missing');
        }
        return id;
    };

    const getElementType = (el: SVGElement): LayerType => {
        switch (el.nodeName.toLowerCase()) {
            case 'text':
                return LayerType.Text;
            case 'image':
                return LayerType.Image;
            default:
                throw new Error(`Unsupported element type: ${el.nodeName}`);
        }
    };

    const getElementRect = useCallback(
        (target: SVGElement): LayerRect => {
            const boundingRect = target.getBoundingClientRect();
            const overlayRect = overlayRef.current?.getBoundingClientRect() as DOMRect;
            return {
                x: boundingRect.x / zoom - overlayRect.x / zoom,
                y: boundingRect.y / zoom - overlayRect.y / zoom,
                width: boundingRect.width / zoom,
                height: boundingRect.height / zoom,
            };
        },
        [zoom],
    );

    const selectLayer = (node: SVGElement) => {
        if (linkedLayer) return;

        const id = getElementId(node);
        const type = getElementType(node);
        const rect = getElementRect(node);
        const selectRect = selectRectRef.current;
        if (selectRect) {
            selectRect.setAttribute('x', String(rect.x));
            selectRect.setAttribute('y', String(rect.y));
            selectRect.setAttribute('width', String(rect.width));
            selectRect.setAttribute('height', String(rect.height));
            selectRect.setAttribute('opacity', '1');
        }
        const props = getTextLayerProps(node);
        setSelectedLayer({ id, type, rect, props, variantId: variant.id });
    };

    const unselectLayer = useCallback(() => {
        selectRectRef.current?.setAttribute('opacity', '0');
        setSelectedLayer(null);
    }, [setSelectedLayer]);

    const highlightLayer = (node: SVGElement) => {
        if (linkedLayer) return;

        const id = getElementId(node);
        const type = getElementType(node);
        const rect = getElementRect(node);
        const highlightRect = highlightRectRef.current;
        if (highlightRect) {
            highlightRect.setAttribute('x', String(rect.x));
            highlightRect.setAttribute('y', String(rect.y));
            highlightRect.setAttribute('width', String(rect.width));
            highlightRect.setAttribute('height', String(rect.height));
            highlightRect.setAttribute('opacity', '1');
        }
        const props = getTextLayerProps(node);
        setHighlightedLayer({ id, type, rect, props, variantId: variant.id });
    };

    const unhighlightLayer = useCallback(() => {
        highlightRectRef.current?.setAttribute('opacity', '0');
        setHighlightedLayer(null);
    }, [setHighlightedLayer]);

    const getClosestSelectableElement = (element: SVGElement): SVGElement => {
        if (element.nodeName.toLowerCase() !== 'tspan') {
            return element;
        }

        return element.closest('text') as SVGElement;
    };

    useEffect(() => {
        if (editedFieldData) {
            const linkedLayer = editedFieldData.linkedLayers?.find((layer) => layer.variant === variant.id);
            setLinkedLayer(linkedLayer || null);
            if (linkedLayer) {
                setSvgScheduledTasks((tasks) => [
                    ...tasks,
                    (svg) => {
                        const node = svg.querySelector(`[data-ntgl-layer-id="${linkedLayer.layerUuid}"]`);
                        if (node instanceof SVGElement) {
                            const rect = getElementRect(node);
                            const linkedLayerRect = linkedLayerRef.current?.querySelector('rect') as SVGRectElement;
                            linkedLayerRect.setAttribute('x', String(rect.x));
                            linkedLayerRect.setAttribute('y', String(rect.y));
                            linkedLayerRect.setAttribute('width', String(rect.width));
                            linkedLayerRect.setAttribute('height', String(rect.height));

                            const layerIcon = linkedLayerRef.current?.querySelector('.layer-icon') as SVGElement;
                            layerIcon.setAttribute('transform', `translate(${rect.x + rect.width - 8}, ${rect.y - 8})`);
                        }
                    },
                ]);
                unhighlightLayer;
                unselectLayer();
                setShowOutlineFor(null);
            } else {
                setShowOutlineFor(fieldToLayerType(editedFieldData.type));
            }
        } else {
            setLinkedLayer(null);
            setShowOutlineFor(null);
        }
    }, [editedFieldData, variant, getElementRect, unhighlightLayer, unselectLayer]);

    return (
        svgContent && (
            <div className="flex flex-col flex-grow-1 basis-1 justify-center">
                <div className="flex flex-row mb-1 gap-2" style={{ width: `${variant.svgFile.width}px` }}>
                    <div
                        className="text-white text-xs overflow-hidden whitespace-nowrap text-ellipsis flex-shrink-0 max-w-[60%]"
                        title={variant.name}
                    >
                        {variant.name}
                    </div>
                    <div
                        className="text-gray-110 text-xs overflow-hidden whitespace-nowrap text-ellipsis max-w-[40%]"
                        title={`${variant.svgFile.width}x${variant.svgFile.height}`}
                    >
                        {`${variant.svgFile.width}x${variant.svgFile.height}`}
                    </div>
                </div>
                <div className="relative pointer-events-none" style={{ zIndex: FRAME_OVERLAY_Z_INDEX }}>
                    {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                    <svg
                        className="absolute"
                        ref={overlayRef}
                        xmlns="http://www.w3.org/2000/svg"
                        width={`${variant.svgFile.width * zoom}px`}
                        height={`${variant.svgFile.height * zoom}px`}
                        viewBox={`0 0 ${variant.svgFile.width} ${variant.svgFile.height}`}
                    >
                        <rect
                            ref={highlightRectRef}
                            className="fill-none stroke-blue-50 stroke-[2px] transition-opacity duration-200"
                            x="0"
                            y="0"
                            width="0"
                            height="0"
                            opacity="0"
                        />
                        <rect
                            ref={selectRectRef}
                            className="fill-none stroke-blue-70 stroke-[2px] transition-opacity duration-200"
                            x="0"
                            y="0"
                            width="0"
                            height="0"
                            opacity="0"
                        />
                        {outlineRects.map((rect, index) => (
                            <rect
                                key={index}
                                className={`fill-none stroke-blue-50 stroke-[2px] transition-opacity duration-200 ${showOutlineFor === LayerType.Text ? 'opacity-100' : 'opacity-0'
                                    }`}
                                strokeDasharray="1"
                                x={rect.x}
                                y={rect.y}
                                width={rect.width}
                                height={rect.height}
                            />
                        ))}
                        <g ref={linkedLayerRef} opacity={linkedLayer ? '1' : '0'}>
                            <rect
                                className="fill-none stroke-green-60 stroke-[2px] transition-opacity duration-200"
                                x="0"
                                y="0"
                                width="0"
                                height="0"
                            />
                            <g className="layer-icon">
                                <LayerIcon />
                            </g>
                        </g>
                    </svg>
                </div>

                {/* biome-ignore lint/a11y/useKeyWithMouseEvents: needs to be updated to make keyboard accessible */}
                <div
                    ref={svgRef}
                    className={`border border-gray-150 [&_svg]:pointer-events-none [&_svg_text]:pointer-events-auto ${linkedLayer ? '[&_svg_text]:cursor-default' : '[&_svg_text]:cursor-pointer'}`}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: will be updated to use a safer method
                    dangerouslySetInnerHTML={{ __html: svgContent }}
                    onMouseOver={(event) => {
                        const target = event.target as HTMLElement;
                        if (
                            target instanceof SVGElement &&
                            SELECTABLE_NODE_TYPES.includes(target.nodeName.toLowerCase())
                        ) {
                            highlightLayer(getClosestSelectableElement(target));
                        } else {
                            unhighlightLayer();
                        }
                    }}
                    onMouseOut={() => {
                        unhighlightLayer();
                    }}
                    onMouseDown={(event) => {
                        const target = event.target as HTMLElement;
                        if (
                            target instanceof SVGElement &&
                            SELECTABLE_NODE_TYPES.includes(target.nodeName.toLowerCase())
                        ) {
                            selectLayer(getClosestSelectableElement(target));
                        } else {
                            unselectLayer();
                        }
                    }}
                />
            </div>
        )
    );
};
